import { LazyLayout, LoadingScreen } from '@nohara-dxdev/react-core';
import { OidcCallbackHandler, OidcLoginRedirect } from '@nohara-dxdev/react-oidc';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { paths } from '@/config/paths';

import { default as AdminRoot, ErrorBoundary as AdminErrorBoundary } from './routes/admin/root';
import { default as AppScopeRoot, ErrorBoundary as AppScopeErrorBoundary } from './routes/app/root';
import { default as OrgRoot, ErrorBoundary as OrgErrorBoundary } from './routes/org/root';
import AppRoot, { ErrorBoundary as AppErrorBoundary } from './routes/root';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;

  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: paths.auth.callback.path,
      element: (
        <OidcCallbackHandler
          successPath={paths.home.path}
          errorPath={paths.error.path}
          element={<LoadingScreen />}
        />
      ),
    },
    {
      path: paths.auth.login.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.auth.logout.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.home.path,
      element: <AppRoot />,
      ErrorBoundary: AppErrorBoundary,
      children: [
        {
          path: '',
          HydrateFallback: LoadingScreen,
          lazy: () => import('./routes/home').then(convert(queryClient)),
        },
        {
          path: paths.admin.root.path,
          element: <AdminRoot />,
          ErrorBoundary: AdminErrorBoundary,
          HydrateFallback: LoadingScreen,
          children: [
            {
              path: paths.admin.dashboard.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/dashboard').then(convert(queryClient)),
            },
            {
              path: paths.admin.users.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/users').then(convert(queryClient)),
            },
            {
              path: paths.admin.orgs.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/orgs').then(convert(queryClient)),
            },
            {
              path: paths.admin.roles.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/roles').then(convert(queryClient)),
            },
            {
              path: paths.admin.apps.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/apps').then(convert(queryClient)),
            },
            {
              path: paths.admin.app.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/app').then(convert(queryClient)),
            },
            {
              path: paths.admin.profile.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/profile').then(convert(queryClient)),
            },
            {
              path: paths.admin.user.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/user').then(convert(queryClient)),
            },
            {
              path: paths.admin.webhooks.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/webhooks').then(convert(queryClient)),
            },
            {
              path: paths.admin.terms.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/terms').then(convert(queryClient)),
            },
            {
              path: paths.admin.termsCreate.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/terms-create').then(convert(queryClient)),
            },
            {
              path: paths.admin.termsDetail.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/admin/terms-detail').then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.org.root.path,
          element: <OrgRoot />,
          ErrorBoundary: OrgErrorBoundary,
          children: [
            {
              path: paths.onboarding.path,
              HydrateFallback: LoadingScreen,
              lazy: () => import('./routes/org/onboarding').then(convert(queryClient)),
            },
            {
              path: paths.org.userProfile.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/org/user').then(convert(queryClient)),
            },
            {
              path: paths.org.selected.path,
              children: [
                {
                  path: paths.org.dashboard.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/dashboard').then(convert(queryClient)),
                },
                {
                  path: paths.org.userProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.users.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/users').then(convert(queryClient)),
                },
                {
                  path: paths.org.roles.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/roles').then(convert(queryClient)),
                },
                {
                  path: paths.org.apps.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/apps').then(convert(queryClient)),
                },
                {
                  path: paths.org.app.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/app').then(convert(queryClient)),
                },
                {
                  path: paths.org.user.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.orgProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/org').then(convert(queryClient)),
                },
                {
                  path: paths.org.webhooks.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/webhooks').then(convert(queryClient)),
                },
                {
                  path: paths.org.webhookDetail.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/webhook-detail').then(convert(queryClient)),
                },
                {
                  path: paths.org.webhookEventDetail.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/webhook-attempts').then(convert(queryClient)),
                },
                {
                  path: paths.org.emailTemplate.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/email-template').then(convert(queryClient)),
                },
              ],
            },
          ],
        },
        {
          path: paths.app.root.path,
          element: <AppScopeRoot />,
          ErrorBoundary: AppScopeErrorBoundary,
          children: [
            {
              path: paths.app.selected.path,
              children: [
                {
                  path: paths.app.dashboard.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/dashboard').then(convert(queryClient)),
                },
                {
                  path: paths.app.organizations.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/organizations').then(convert(queryClient)),
                },
                {
                  path: paths.app.appAssignment.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/app-assignment').then(convert(queryClient)),
                },
                {
                  path: paths.app.roles.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/roles').then(convert(queryClient)),
                },
                {
                  path: paths.app.users.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/users').then(convert(queryClient)),
                },
                {
                  path: paths.app.user.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/user').then(convert(queryClient)),
                },
                {
                  path: paths.app.appProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/profile').then(convert(queryClient)),
                },
                {
                  path: paths.app.emailTemplate.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/email-template').then(convert(queryClient)),
                },
                {
                  path: paths.app.webhooks.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/webhooks').then(convert(queryClient)),
                },
                {
                  path: paths.app.webhookDetail.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/webhook-detail').then(convert(queryClient)),
                },
                {
                  path: paths.app.webhookEventDetail.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/webhook-attempts').then(convert(queryClient)),
                },
                {
                  path: paths.app.terms.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/terms').then(convert(queryClient)),
                },
                {
                  path: paths.app.termsDetail.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/app/terms-detail').then(convert(queryClient)),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '*',
      HydrateFallback: LoadingScreen,
      lazy: () => import('./routes/not-found').then(convert(queryClient)),
    },
  ]);

export const AppRouter = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};
